import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PencilIcon, CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import DropdownCustom from '../../../moduleComponents/DropdownCustom';
import { toast } from 'react-hot-toast';
import { getL1Options, getL2Options, buildL1Key } from '../../../moduleServices/helper_hierarchicalCategory';
import { createBudgetPlanItem, updateBudgetPlanItem, deleteBudgetPlanItem } from '../../viewServices/budget_api';


const normalizeValue = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  return val.value || val.label || '';
};

// ############### COMPONENT FLOATING MENU ACTIONS ###############
const FloatingMenu = ({ buttonRef, onConfirm, onDelete, onCancel, isNew, loading }) => {
  if (!buttonRef?.current) return null;

  const rect = buttonRef.current.getBoundingClientRect();
  
  // Calculate menu width dynamically: 2 buttons (isNew) or 3 buttons (edit)
  // Each button ~24px (w-6) + gap-1.5 (~6px) + padding px-1.5 (~12px total)
  const menuWidth = isNew ? 66 : 96; // 2 buttons: ~66px, 3 buttons: ~96px
  
  const position = {
    position: 'absolute',
    top: `${rect.bottom + window.scrollY + 2}px`,
    left: `${rect.right + window.scrollX - menuWidth}px`,
    zIndex: 40,
  };

  const btnC = 'flex items-center justify-center p-0.5 h-6 w-6 rounded focus:outline-none ' + (loading ? 'cursor-not-allowed' : 'cursor-pointer');

  return createPortal(
    <div style={position} className="bg-white border border-gray-200 rounded-xl shadow-lg p-0.5 px-1.5 flex gap-1.5">
      <button 
        className={btnC + " text-gray-400 hover:bg-gray-50"} 
        onClick={onCancel} 
        aria-label="Cancel" 
        disabled={loading}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
      <button 
        className={btnC + " text-green-700 hover:bg-green-50"} 
        onClick={onConfirm} 
        aria-label="Save" 
        disabled={loading}
      >
        <CheckIcon className="h-4 w-4" />
      </button>
      {!isNew && !loading && (
        <button 
          className={btnC + " text-red-500 hover:bg-red-50"} 
          onClick={onDelete} 
          aria-label="Delete entry" 
          type="button"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </div>,
    document.body
  );
};


// ############### COMPONENT CARD ROW CLASS L2 ###############
const CardRowClassL2 = ({
  planId,
  budget = {},
  isNew = false,
  index = null,
  hierarchicalCategories = {},
  onItemCreated,
  onItemUpdated,
  onItemDeleted,
  onCancelAdd,
  l0Id,
  l1Id,
  date,
}) => {

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const menuRef = useRef(null);

  const [fields, setFields] = useState(() => ({
    class_l0: budget.class_l0 || l0Id || '',
    class_l1: budget.class_l1 || l1Id || '',
    class_l2: normalizeValue(budget.class_l2),
    description: budget.description || '',
    budget_amount: budget.budget_amount || '',
    budget_type: budget.budget_type || 'Out',
  }));

  const [origFields, setOrigFields] = useState(fields);


  // ############### EFFECTS AND BASIC FUNCTIONS ###############
  useEffect(() => {
    if (!editing) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        const isClickInsideMenu = e.target.closest('.bg-white.border');
        if (!isClickInsideMenu) {
          handleCancel();
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing]);

  useEffect(() => {
    const init = {
      class_l0: budget.class_l0 || l0Id || '',
      class_l1: budget.class_l1 || l1Id || '',
      class_l2: normalizeValue(budget.class_l2),
      description: budget.description || '',
      budget_amount: budget.budget_amount || '',
      budget_type: budget.budget_type || 'Out',
    };
    setFields(init);
    setOrigFields(init);
  }, [budget.id, l0Id, l1Id]);


  function isDirty(orig, novo) {
    return ['class_l2', 'description', 'budget_amount', 'budget_type']
      .some(f => String(orig[f] ?? '') !== String(novo[f] ?? ''));
  }

  function handleChangeL2(e) {
    setFields(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function handleEdit() {
    setEditing(true);
  }

  function handleCancel() {
    if (isNew) {
      onCancelAdd && onCancelAdd(index);
    } else {
      setEditing(false);
      setFields(origFields);
    }
  }


  // ############### HANDLERS SAVE/DELETE ###############
  async function handleSaveL2() {
    let { class_l0, class_l1, class_l2, description, budget_amount, budget_type } = fields;
    description = description && description.trim() ? description.trim() : '-';
    budget_amount = String(budget_amount).replace(',', '.');
    
    if (!budget_amount || isNaN(Number(budget_amount))) {
      toast.error('Provide a valid numeric value.');
      return;
    }
    
    if (!class_l2 || !class_l2.trim()) {
      toast.error('Please select a category L2.');
      return;
    }

    const l2Name = hierarchicalCategories.by_l2?.[normalizeValue(class_l2)]?.name || normalizeValue(class_l2);

    const budgetData = {
      plan: budget.plan ?? planId,
      class_l0,
      class_l1,
      class_l2: l2Name,
      description,
      budget_amount,
      budget_type,
      budget_currency: 'BRL',
    };

    if (isNew) {
      setLoading(true);
      try {
        const res = await createBudgetPlanItem(budgetData);
        toast.success('Budget created!');
        if (onItemCreated) onItemCreated(res?.data ?? null);
      } catch {
        toast.error('Failed to create. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      if (!isDirty(origFields, fields)) {
        setEditing(false);
        return;
      }
      setLoading(true);
      try {
        const res = await updateBudgetPlanItem(budget.id, budgetData);
        toast.success('Budget updated!');
        setEditing(false);
        const updated = res?.data ?? { ...budget, ...budgetData, id: budget.id };
        if (onItemUpdated) onItemUpdated(updated);
      } catch {
        toast.error('Failed to save. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }

  async function handleDeleteL2() {
    if (isNew) {
      onCancelAdd && onCancelAdd(index);
      return;
    }
    setLoading(true);
    try {
      await deleteBudgetPlanItem(budget.id);
      toast.success('Budget deleted!');
      if (onItemDeleted) onItemDeleted(budget.id);
    } catch {
      toast.error('Failed to delete. Please try again.');
    } finally {
      setLoading(false);
    }
  }


  // ############### RENDERERS ###############
  const disabledStyle = loading ? { opacity: 0.55, cursor: 'not-allowed' } : {};
  const btnC = 'flex items-center justify-center p-1 h-7 w-7 rounded focus:outline-none gap-1 ' + (loading ? 'cursor-not-allowed' : 'cursor-pointer');

  function renderEditMode() {
    const l1Key = fields.class_l0 && fields.class_l1 
      ? buildL1Key(
          hierarchicalCategories.by_l0?.[fields.class_l0]?.name || fields.class_l0,
          fields.budget_type,
          hierarchicalCategories.by_l1?.[fields.class_l1]?.name || fields.class_l1
        )
      : '';

  return (
      <div className="flex items-center gap-1 w-full min-w-0">
        <DropdownCustom
          options={getL2Options(hierarchicalCategories, l1Key)}
          selectedValue={fields.class_l2}
          onSelect={option => setFields(f => ({ ...f, class_l2: normalizeValue(option) }))}
          placeholder="Sub-Category"
          width="w-32"
          buttonClassName="h-6 m-0.5 mr-0"
            disabled={loading}
          usePortal={true}
          />
          <input
          className="text-xs px-2 border border-gray-300 rounded-md flex-1 min-w-0 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all h-6"
            name="description"
            value={fields.description}
          onChange={handleChangeL2}
          placeholder="Description"
          aria-label="Description"
            disabled={loading}
          />
          <input
          ref={menuRef}
          className="text-xs px-2 border border-gray-300 rounded-md w-20 bg-white ml-auto mr-0.5 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all h-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            name="budget_amount"
            value={fields.budget_amount}
          onChange={handleChangeL2}
          placeholder="Value"
          aria-label="Value"
            type="number"
            pattern="[0-9.,]*"
            step="0.01"
            disabled={loading}
          />
      </div>
    );
  }
  function renderViewMode() {
    const l2Value = normalizeValue(fields.class_l2);
    const l2Label = hierarchicalCategories?.by_l2?.[l2Value]?.name || l2Value || '-';
    const amount = parseFloat(fields.budget_amount || 0);
    const formattedAmount = amount.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 });
    
    let displayValue;
    if (amount === 0) {
      displayValue = <span className="text-gray-400">{formattedAmount}</span>;
    } else {
      displayValue = <>{(fields.budget_type === 'In' ? '+' : '-')} {formattedAmount}</>;
    }
    
    return <>
      <span className="text-xs text-gray-600 font-medium flex-1">
        {l2Label} {' | '} {fields.description || '-'}
          </span>
      <span className="text-xs min-w-[100px] text-right mr-2">
        {displayValue}
          </span>
      <button ref={menuRef} className={btnC + " text-gray-400 hover:text-green-700"} onClick={() => setEditing(true)} aria-label="Edit" disabled={loading}><PencilIcon className="h-4 w-4" /></button>
    </>;
  }

  return (
    <>
      <div className="flex items-center gap-1 w-full overflow-x-auto" style={disabledStyle}>
        {editing ? renderEditMode() : renderViewMode()}
    </div>
      {editing && (
        <FloatingMenu 
          buttonRef={menuRef} 
          onConfirm={handleSaveL2}
          onDelete={handleDeleteL2}
          onCancel={handleCancel}
          isNew={isNew}
          loading={loading}
        />
      )}
    </>
  );
};

export default CardRowClassL2;
